import ApartmentIcon from '@mui/icons-material/Apartment';
import BusinessIcon from '@mui/icons-material/Business';
import ContactPhoneIcon from '@mui/icons-material/ContactPhone';
import { Box, Chip, Grid, Paper, Typography } from '@mui/material';
import { Business, BusinessUnit } from 'lib/types/models';
import React from 'react';

interface BusinessCardProps {
  business: Business | BusinessUnit;
  isUnit?: boolean;
  onClick: () => void;
  children?: React.ReactNode;
}

const BusinessCard: React.FC<BusinessCardProps> = ({
  business,
  isUnit = false,
  onClick,
  children,
}) => {
  const getBusinessData = () => {
    if (isUnit && 'business' in business) {
      return business.business;
    }
    return business as Business;
  };

  const getIcon = () => {
    if (isUnit) {
      return (
        <ApartmentIcon sx={{ fontSize: '0.8rem', color: 'secondary.main' }} />
      );
    }
    return <BusinessIcon sx={{ fontSize: '0.8rem', color: 'primary.main' }} />;
  };

  const getIconBackground = () => {
    if (isUnit) {
      return 'rgba(156, 39, 176, 0.1)';
    }
    return 'rgba(25, 118, 210, 0.1)';
  };

  const getTitle = () => {
    if (isUnit) {
      return 'Unidad de Negocio';
    }
    return 'Empresa Principal';
  };

  const getChipProps = () => {
    if (isUnit) {
      return {
        label: 'Unidad',
        color: 'secondary' as const,
        variant: 'outlined' as const,
      };
    }
    return {
      label: 'Principal',
      color: 'primary' as const,
    };
  };

  const getBusinessName = () => {
    if (isUnit && 'unitCode' in business) {
      return `${business.name} (${business.unitCode})`;
    }
    return getBusinessData().name;
  };

  const getContactsInfo = () => {
    if (isUnit && 'contacts' in business && business.contacts) {
      const contactCount = business.contacts.length;
      return `${contactCount} contacto${contactCount !== 1 ? 's' : ''} disponible${contactCount !== 1 ? 's' : ''}`;
    }
    return null;
  };

  return (
    <Paper
      sx={{
        p: 2,
        boxShadow: 'none',
        border: '1px solid #e0e0e0',
        cursor: 'pointer',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          borderColor: 'primary.main',
          transform: 'translateY(-2px)',
        },
      }}
      onClick={onClick}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: 1,
          mb: 1.5,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            color: 'text.primary',
            fontSize: '1rem',
            lineHeight: 1.1,
          }}
        >
          {getTitle()}
        </Typography>
        <Chip
          {...getChipProps()}
          size="small"
          sx={{ height: '18px', fontSize: '0.65rem' }}
        />
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Box display="flex" alignItems="center" gap={1}>
            <Box
              sx={{
                backgroundColor: getIconBackground(),
                borderRadius: '50%',
                p: 0.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {getIcon()}
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: '0.65rem' }}
              >
                {isUnit ? 'Nombre de Unidad' : 'Razón Social'}
              </Typography>
              <Typography
                variant="body2"
                fontWeight="medium"
                sx={{ fontSize: '0.75rem' }}
              >
                {getBusinessName()}
              </Typography>
            </Box>
          </Box>
        </Grid>
        {getContactsInfo() && (
          <Grid item xs={12}>
            <Box display="flex" alignItems="center" gap={1}>
              <Box
                sx={{
                  backgroundColor: 'rgba(76, 175, 80, 0.1)',
                  borderRadius: '50%',
                  p: 0.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <ContactPhoneIcon
                  sx={{ fontSize: '0.8rem', color: 'success.main' }}
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontSize: '0.65rem' }}
                >
                  Contactos
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight="medium"
                  sx={{ fontSize: '0.75rem' }}
                >
                  {getContactsInfo()}
                </Typography>
              </Box>
            </Box>
          </Grid>
        )}

        {children && (
          <Grid item xs={12}>
            {children}
          </Grid>
        )}
      </Grid>
    </Paper>
  );
};

export default BusinessCard;
